import { request } from '@/utils/ajax';
import {
    BygrProject,
    PaginationResult,
    BygrProjectDetails,
    PaginatedSearchRequest,
    ProjectDashboardData,
    ProjectUnit,
    BygrModule,
    CreateSubProjectDto,
} from '@/types';
import { toQueryString } from '@/utils/url';
import { LandingPageImageDTO } from '@ygtechnologies/bygr-frontend-component-library';

/** `sellerProfileId` narrows the list to one seller profile's projects. */
export function getProjects(
    req: PaginatedSearchRequest,
    sellerProfileId?: number | null
): Promise<PaginationResult<BygrProject>> {
    return request(`api/project`, {
        query: sellerProfileId != null ? { ...req, sellerProfileId } : req,
    }).then((x) => x.json());
}
export function getProjectsExportUrl(req: PaginatedSearchRequest) {
    return `api/project/excel?${toQueryString(req)}`;
}
export function getProjectDetails(id: number): Promise<BygrProjectDetails> {
    return request(`api/project/${id}`).then((x) => x.json());
}
export function getProjectDashboardData(id: number): Promise<ProjectDashboardData> {
    return request(`api/project/${id}/dashboard`).then((x) => x.json());
}
export function getProjectUnits(id: number, req: PaginatedSearchRequest): Promise<PaginationResult<ProjectUnit>> {
    return request(`api/project/${id}/units`, { query: req }).then((x) => x.json());
}
export function getProjectUnitsExportUrl(id: number, req: PaginatedSearchRequest) {
    return `api/project/${id}/units/excel?${toQueryString(req)}`;
}

export function setModule(projectId: number, req: SetModuleRequest) {
    return request(`api/project/${projectId}/module`, {
        method: 'POST',
        body: req,
        json: true,
    });
}

export async function getAvailableDocumentCategories(projectId: number): Promise<AvailableDocumentCategoriesDto> {
    return await request(`api/project/${projectId}/availableDocumentCategories`, {
        method: 'GET',
        json: true,
    }).then((x) => x.json());
}

export async function setDocumentCategoryHierarchy(
    projectId: number,
    categoryHierarchyId: number
): Promise<AvailableDocumentCategoriesDto> {
    return await request(`api/project/${projectId}/documentCategoryHierarchy/${categoryHierarchyId}`, {
        method: 'POST',
        json: true,
    }).then((x) => x.json());
}

export function getLandingPageData(projectId: number): Promise<ProjectLandingPageData> {
    return request(`api/project/${projectId}/landing-page`).then((x) => x.json());
}

export function getConstructionPhases(projectId: number): Promise<ProjectConstructionPhase[]> {
    return request(`api/project/${projectId}/construction-phases`).then((x) => x.json());
}

export async function createSubProject(req: CreateSubProjectDto): Promise<void> {
    await request(`api/project/${req.parentProjectId}/create-sub-project`, {
        method: 'POST',
        body: req,
        json: true,
    });
}

export type FdvTransferResult = {
    documentsExpected: number;
    documentsCreated: number;
    documentsAlreadyPresent: number;
    documentsSkippedNoUploader: number;
    unitLinksCreated: number;
    unitLinksAlreadyPresent: number;
    roomTagsCreated: number;
    documentsWithoutRoom: number;
    unitsCovered: number;
    signedOrders: number;
    orderedProducts: number;
    filesOnOrderedProducts: number;
    pdfFilesOnOrderedProducts: number;
};

export async function transfereFDVFromAmToCM(projectId: number): Promise<FdvTransferResult> {
    const response = await request(`api/project/${projectId}/transfer-fdv`, {
        method: 'POST',
        json: true,
    });
    return response.json();
}
export type AvailableDocumentCategoriesDto = {
    selectedHierarchy?: number;
    categories: DocumentCategoriesDto[];
    hasLinkedDocuments: boolean;
};

export type DocumentCategoriesDto = {
    id: number;
    name: string;
};

export interface ProjectConstructionPhase {
    id: number;
    name: string;
}
export interface ResidentialUnitLinkInfo {
    residentialUnitId: number;
    residentialUnitName: string;
    //linkedGroups: LinkedCatalogGroup[];
    residentialUnitGroupId: number | null;
    residentialUnitGroupName: string;
    layoutType: string;
    floor: string;
    buyerModules: BygrModule;
    //tags: ProjectTag[];
}

export function getResidentialUnitLinkInfo(projectId: number): Promise<ResidentialUnitLinkInfo[]> {
    return request(`api/project/${projectId}/units-link-info`).then((x) => x.json());
}

export interface SetModuleRequest {
    module: BygrModule;
    isActive: boolean;
}

interface AdminLandingPageImageDTO extends LandingPageImageDTO {
    constructionPhaseId: number | null;
    userFileId: number | null;
}

export interface ProjectLandingPageData {
    cmImages: AdminLandingPageImageDTO[];
    amImages: AdminLandingPageImageDTO[];
}

export interface FollowerRecalculationPreview {
    wouldRemove: number;
    wouldRevive: number;
    wouldAdd: number;
    unchanged: number;
}

export function previewRecalculateFollowers(projectId: number): Promise<FollowerRecalculationPreview> {
    return request(`api/project/${projectId}/recalculate-followers/preview`, { method: 'POST' }).then((x) => x.json());
}

export function recalculateFollowers(projectId: number): Promise<Response> {
    return request(`api/project/${projectId}/recalculate-followers`, { method: 'POST' });
}

export function recalculateFollowersForAllProjects(fromProjectId: number): Promise<Response> {
    return request(`api/project/recalculate-followers/all`, {
        method: 'POST',
        query: { fromProjectId },
    });
}

export interface FollowerRepairProject {
    projectId: number;
    projectName: string;
    deadFollows: number;
}

export interface FollowerRepairProgress {
    repairableDeadFollows: number;
    projectsRemaining: number;
    lowestProjectIdRemaining: number | null;
    projects: FollowerRepairProject[];
}

export function getFollowerRepairProgress(): Promise<FollowerRepairProgress> {
    return request(`api/project/follower-repair-progress`).then((x) => x.json());
}

export type DocumentCategorisationOutcome =
    | 'Categorised'
    | 'CopiedFromExisting'
    | 'NoCategoryFound'
    | 'NotPdf'
    | 'NoText'
    | 'AiError';

export type DocumentCategorisationStatus = 'Queued' | 'Running' | 'Completed' | 'Failed' | 'Interrupted';

export interface DocumentCategorisationOutcomeCount {
    outcome: DocumentCategorisationOutcome;
    files: number;
    documents: number;
}

export interface DocumentCategorisationRun {
    id: number;
    projectId: number;
    status: DocumentCategorisationStatus;
    startedBy: string | null;
    createdAt: string | null;
    startedAt: string | null;
    finishedAt: string | null;
    lastProgressAt: string | null;
    categoryTableName: string | null;
    maxCategories: number;
    documentCount: number;
    groupCount: number;
    groupsProcessed: number;
    documentsUpdated: number;
    outcomes: DocumentCategorisationOutcomeCount[];
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    costUsd: number;
    errorMessage: string | null;
    undoneAt: string | null;
    undoneBy: string | null;
    categoriesRemoved: number | null;
}

export interface DocumentCategorisationUndoPreview {
    runId: number;
    categoriesToRemove: number;
    documentsAffected: number;
    documentsChangedSince: number;
}

export interface DocumentCategorisationPreview {
    projectId: number;
    categoryTableName: string | null;
    hasExplicitCategoryTable: boolean;
    isDeprecatedCategoryTable: boolean;
    categoryCount: number;
    documentCount: number;
    uncategorisedDocumentCount: number;
    groupsToProcess: number;
    groupsToCopyFromExisting: number;
    groupsToAnalyse: number;
    nonPdfGroups: number;
    estimatedInputTokens: number;
    estimatedOutputTokens: number;
    estimatedCostUsd: number;
    canStart: boolean;
    cannotStartReason: string | null;
    activeRun: DocumentCategorisationRun | null;
}

export function previewDocumentCategorisation(projectId: number): Promise<DocumentCategorisationPreview> {
    return request(`api/project/${projectId}/document-categorisation/preview`).then((x) => x.json());
}

export function startDocumentCategorisation(projectId: number, maxCategories: number): Promise<DocumentCategorisationRun> {
    return request(`api/project/${projectId}/document-categorisation`, {
        method: 'POST',
        query: { maxCategories },
    }).then((x) => x.json());
}

/** Null when the project has never had a categorisation run. */
export async function getLatestDocumentCategorisation(projectId: number): Promise<DocumentCategorisationRun | null> {
    const response = await request(`api/project/${projectId}/document-categorisation/latest`);
    return response.status === 204 ? null : response.json();
}

export function previewUndoDocumentCategorisation(
    projectId: number,
    runId: number
): Promise<DocumentCategorisationUndoPreview> {
    return request(`api/project/${projectId}/document-categorisation/${runId}/undo`).then((x) => x.json());
}

export function undoDocumentCategorisation(projectId: number, runId: number): Promise<DocumentCategorisationRun> {
    return request(`api/project/${projectId}/document-categorisation/${runId}/undo`, { method: 'POST' }).then((x) =>
        x.json()
    );
}

export function getDocumentCategorisationReportUrl(projectId: number, runId: number) {
    return `api/project/${projectId}/document-categorisation/${runId}/excel`;
}
